import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { 
  Eye, 
  EyeOff, 
  Loader2, 
  Building, 
  User, 
  MapPin, 
  Phone, 
  Mail,
  Globe,
  Factory,
  Briefcase,
  ChevronDown,
  Search,
  Check
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API = "/api";

// US States list
const US_STATES = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
  { value: "DC", label: "District of Columbia" },
];

// Industries list (matching LeadFormModal)
const INDUSTRIES = [
  "Agriculture",
  "Automotive",
  "Banking & Financial Services",
  "Biotechnology",
  "Construction",
  "Consulting",
  "Consumer Goods",
  "Education",
  "Energy & Utilities",
  "Entertainment & Media",
  "Food & Beverage",
  "Government",
  "Healthcare",
  "Home Services",
  "Hospitality & Tourism",
  "Information Technology",
  "Insurance",
  "Legal Services",
  "Logistics & Transportation",
  "Manufacturing",
  "Marketing & Advertising",
  "Mining & Metals",
  "Non-Profit",
  "Pharmaceuticals",
  "Professional Services",
  "Real Estate",
  "Retail",
  "Telecommunications",
  "Wholesale & Distribution",
];

// Searchable Select Component
const SearchableSelect = ({ 
  options, 
  value, 
  onChange, 
  placeholder,
  testId 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);

  const filteredOptions = options.filter(opt => 
    opt.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full h-12 bg-[#1a1a1a] border border-[#333333] rounded-lg px-4 text-left text-white focus:outline-none focus:border-[#14A800] flex items-center justify-between"
        data-testid={testId}
      >
        <span className={value ? "text-white" : "text-[#666666]"}>
          {value || placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 text-[#666666] transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>
      
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-[#1a1a1a] border border-[#333333] rounded-lg shadow-xl overflow-hidden">
          <div className="p-2 border-b border-[#333333]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666666]" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
                className="w-full bg-[#0D0D0D] border border-[#333333] rounded-lg pl-10 pr-3 py-2 text-sm text-white placeholder-[#666666] focus:outline-none focus:border-[#14A800]"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => {
                    onChange(opt);
                    setIsOpen(false);
                    setSearch("");
                  }}
                  className={`w-full px-4 py-2.5 text-sm text-left hover:bg-[#2A2A2A] flex items-center justify-between ${
                    value === opt ? "bg-[#14A800]/10 text-[#14A800]" : "text-white"
                  }`}
                >
                  {opt}
                  {value === opt && <Check className="w-4 h-4" />}
                </button>
              ))
            ) : (
              <div className="px-4 py-2.5 text-sm text-[#666666]">No results found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default function Signup() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Step 1: Account Info
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  
  // Step 2: Contact Info
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [mobilePhone, setMobilePhone] = useState("");
  
  // Step 3: Company Info
  const [companyName, setCompanyName] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [country, setCountry] = useState("USA");
  const [companyPhone, setCompanyPhone] = useState("");
  const [companyEmail, setCompanyEmail] = useState("");
  const [industry, setIndustry] = useState("");

  // Format phone number as (XXX)XXX-XXXX
  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) {
      return digits.length > 0 ? `(${digits}` : '';
    } else if (digits.length <= 6) {
      return `(${digits.slice(0, 3)})${digits.slice(3)}`;
    } else {
      return `(${digits.slice(0, 3)})${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
  };

  // Format ZIP code
  const formatZipCode = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 5) {
      return digits;
    } else {
      return `${digits.slice(0, 5)}-${digits.slice(5, 9)}`;
    }
  };

  const handleStep1Next = () => {
    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setStep(2);
  };

  const handleStep2Next = () => {
    if (!firstName || !lastName) {
      toast.error("Please fill in your name");
      return;
    }
    setStep(3);
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    
    if (!companyName || !industry) {
      toast.error("Please fill in company name and industry");
      return;
    }

    setLoading(true);
    
    let lastError = null;
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 15000);
        
        const response = await fetch(`${API}/auth/signup`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: `${firstName} ${lastName}`,
            email,
            password,
            first_name: firstName,
            last_name: lastName,
            job_title: jobTitle,
            mobile_phone: mobilePhone,
            company_name: companyName,
            address_line_1: addressLine1,
            address_line_2: addressLine2,
            city,
            state,
            zip_code: zipCode,
            country,
            company_phone: companyPhone,
            company_email: companyEmail,
            industry,
          }),
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);

        if (!response.ok) {
          const errorData = await response.json();
          toast.error(errorData.detail || "Signup failed");
          setLoading(false);
          return;
        }

        const data = await response.json();
        localStorage.setItem("auth_token", data.access_token);
        localStorage.setItem("user", JSON.stringify(data.user));
        toast.success("Account created successfully!");
        navigate("/chat", { replace: true });
        return;
        
      } catch (error) {
        lastError = error;
        console.error(`Signup attempt ${attempt} failed:`, error);
        
        if (attempt < 3) {
          await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
        }
      }
    }
    
    if (lastError?.name === 'AbortError') {
      toast.error("Request timed out. Please check your internet connection.");
    } else {
      toast.error("Unable to connect to server. Please try again.");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Georgia, serif' }}>
            Get started
          </h1>
          <p className="text-[#888888]">
            {step === 1 && "Create your account"}
            {step === 2 && "Tell us about yourself"}
            {step === 3 && "Tell us about your company"}
          </p>
          
          {/* Progress indicator */}
          <div className="flex items-center justify-center gap-2 mt-4">
            <div className={`w-8 h-1 rounded-full transition-colors ${step >= 1 ? 'bg-[#14A800]' : 'bg-[#333]'}`} />
            <div className={`w-8 h-1 rounded-full transition-colors ${step >= 2 ? 'bg-[#14A800]' : 'bg-[#333]'}`} />
            <div className={`w-8 h-1 rounded-full transition-colors ${step >= 3 ? 'bg-[#14A800]' : 'bg-[#333]'}`} />
          </div>
        </div>

        {/* Step 1: Account Info */}
        {step === 1 && (
          <div className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white font-medium">Email</Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666666]" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="john@company.com"
                  className="h-12 pl-11 bg-[#1a1a1a] border-[#333333] text-white placeholder:text-[#666666] focus:border-[#14A800] focus:ring-0 rounded-lg"
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white font-medium">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Create a password (min. 6 characters)"
                  className="h-12 bg-[#1a1a1a] border-[#333333] text-white placeholder:text-[#666666] focus:border-[#14A800] focus:ring-0 rounded-lg pr-12"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#666666] hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              onClick={handleStep1Next}
              className="w-full h-12 bg-[#14A800] hover:bg-[#108A00] text-white font-medium rounded-full transition-all duration-200"
              data-testid="next-step-btn"
            >
              Continue
            </Button>

            <p className="text-center text-[#888888]">
              Already have an account?{" "}
              <Link to="/login" className="text-[#14A800] hover:underline font-medium">
                Log in
              </Link>
            </p>
          </div>
        )}

        {/* Step 2: Contact Info */}
        {step === 2 && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName" className="text-white font-medium">First Name *</Label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666666]" />
                  <Input
                    id="firstName"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="John"
                    className="h-12 pl-11 bg-[#1a1a1a] border-[#333333] text-white placeholder:text-[#666666] focus:border-[#14A800] focus:ring-0 rounded-lg"
                    data-testid="first-name-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName" className="text-white font-medium">Last Name *</Label>
                <Input
                  id="lastName"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Doe"
                  className="h-12 bg-[#1a1a1a] border-[#333333] text-white placeholder:text-[#666666] focus:border-[#14A800] focus:ring-0 rounded-lg"
                  data-testid="last-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="jobTitle" className="text-white font-medium">Job Title</Label>
              <div className="relative">
                <Briefcase className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666666]" />
                <Input
                  id="jobTitle"
                  type="text"
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                  placeholder="CEO, Operations Manager, etc."
                  className="h-12 pl-11 bg-[#1a1a1a] border-[#333333] text-white placeholder:text-[#666666] focus:border-[#14A800] focus:ring-0 rounded-lg"
                  data-testid="job-title-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="mobilePhone" className="text-white font-medium">Mobile Phone</Label>
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666666]" />
                <Input
                  id="mobilePhone"
                  type="tel"
                  value={mobilePhone}
                  onChange={(e) => setMobilePhone(formatPhoneNumber(e.target.value))}
                  placeholder="(619)555-0123"
                  maxLength={14}
                  className="h-12 pl-11 bg-[#1a1a1a] border-[#333333] text-white placeholder:text-[#666666] focus:border-[#14A800] focus:ring-0 rounded-lg"
                  data-testid="mobile-phone-input"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                type="button"
                onClick={() => setStep(1)}
                variant="outline"
                className="flex-1 h-12 bg-transparent border-[#333333] text-white hover:bg-[#1a1a1a] rounded-full"
              >
                Back
              </Button>
              <Button
                onClick={handleStep2Next}
                className="flex-1 h-12 bg-[#14A800] hover:bg-[#108A00] text-white font-medium rounded-full transition-all duration-200"
                data-testid="next-step-2-btn"
              >
                Continue
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Company Info */}
        {step === 3 && (
          <form onSubmit={handleSignup} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="companyName" className="text-white font-medium">Company Name *</Label>
              <div className="relative">
                <Building className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666666]" />
                <Input
                  id="companyName"
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Acme Corporation"
                  className="h-12 pl-11 bg-[#1a1a1a] border-[#333333] text-white placeholder:text-[#666666] focus:border-[#14A800] focus:ring-0 rounded-lg"
                  data-testid="company-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="addressLine1" className="text-white font-medium">Address Line 1</Label>
              <div className="relative">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666666]" />
                <Input
                  id="addressLine1"
                  type="text"
                  value={addressLine1}
                  onChange={(e) => setAddressLine1(e.target.value)}
                  placeholder="Street address or P.O. Box"
                  className="h-12 pl-11 bg-[#1a1a1a] border-[#333333] text-white placeholder:text-[#666666] focus:border-[#14A800] focus:ring-0 rounded-lg"
                  data-testid="address-1-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="addressLine2" className="text-white font-medium">Address Line 2</Label>
              <Input
                id="addressLine2"
                type="text"
                value={addressLine2}
                onChange={(e) => setAddressLine2(e.target.value)}
                placeholder="Apartment, suite, unit, or building"
                className="h-12 bg-[#1a1a1a] border-[#333333] text-white placeholder:text-[#666666] focus:border-[#14A800] focus:ring-0 rounded-lg"
                data-testid="address-2-input"
              />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label htmlFor="city" className="text-white font-medium">City</Label>
                <Input
                  id="city"
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="San Diego"
                  className="h-12 bg-[#1a1a1a] border-[#333333] text-white placeholder:text-[#666666] focus:border-[#14A800] focus:ring-0 rounded-lg"
                  data-testid="city-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state" className="text-white font-medium">State</Label>
                <Select value={state} onValueChange={setState}>
                  <SelectTrigger className="h-12 bg-[#1a1a1a] border-[#333333] text-white focus:ring-0 focus:border-[#14A800] rounded-lg" data-testid="state-select">
                    <SelectValue placeholder="State" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-[#333333] max-h-60">
                    {US_STATES.map((s) => (
                      <SelectItem key={s.value} value={s.value} className="text-white hover:bg-[#2A2A2A] focus:bg-[#2A2A2A]">
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="zipCode" className="text-white font-medium">ZIP</Label>
                <Input
                  id="zipCode"
                  type="text"
                  value={zipCode}
                  onChange={(e) => setZipCode(formatZipCode(e.target.value))}
                  placeholder="92101"
                  maxLength={10}
                  className="h-12 bg-[#1a1a1a] border-[#333333] text-white placeholder:text-[#666666] focus:border-[#14A800] focus:ring-0 rounded-lg"
                  data-testid="zip-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="companyPhone" className="text-white font-medium">Company Phone</Label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666666]" />
                  <Input
                    id="companyPhone"
                    type="tel"
                    value={companyPhone}
                    onChange={(e) => setCompanyPhone(formatPhoneNumber(e.target.value))}
                    placeholder="(619)555-0100"
                    maxLength={14}
                    className="h-12 pl-11 bg-[#1a1a1a] border-[#333333] text-white placeholder:text-[#666666] focus:border-[#14A800] focus:ring-0 rounded-lg"
                    data-testid="company-phone-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="companyEmail" className="text-white font-medium">Company Email</Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666666]" />
                  <Input
                    id="companyEmail"
                    type="email"
                    value={companyEmail}
                    onChange={(e) => setCompanyEmail(e.target.value)}
                    placeholder="info@company.com"
                    className="h-12 pl-11 bg-[#1a1a1a] border-[#333333] text-white placeholder:text-[#666666] focus:border-[#14A800] focus:ring-0 rounded-lg"
                    data-testid="company-email-input"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-white font-medium">Industry *</Label>
              <SearchableSelect
                options={INDUSTRIES}
                value={industry}
                onChange={setIndustry}
                placeholder="Select your industry"
                testId="industry-select"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                onClick={() => setStep(2)}
                variant="outline"
                className="flex-1 h-12 bg-transparent border-[#333333] text-white hover:bg-[#1a1a1a] rounded-full"
              >
                Back
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 h-12 bg-[#14A800] hover:bg-[#108A00] text-white font-medium rounded-full transition-all duration-200"
                data-testid="signup-submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  "Create Account"
                )}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
